import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import PendingActions from '@ckeditor/ckeditor5-core/src/pendingactions';

/**
 * Source Editing Availability Plugin — Decouples editSource command from autosave locks.
 * - For RHP (mini-editor): forces isEnabled=true to ensure immediate availability on tab switch
 * - For main editor: only patches forceDisabled to ignore sourceEditingEnhanced lock
 */
export default class SourceEditingAvailability extends Plugin {
  static get pluginName() {
    return 'SourceEditingAvailability';
  }

  static get requires() {
    return [PendingActions];
  }

  init() {
    const { editor } = this;
    const pendingActions = editor.plugins.get(PendingActions);
    const editorType = editor.config.get('type');
    const isMiniEditor = editorType === 'mini-editor';

    // Patch editSource command to decouple from autosave locks.
    const patchEditSourceCommand = () => {
      const editSourceCommand = editor.commands.get('editSource');
      if (!editSourceCommand || editSourceCommand.rhpPatched) {
        return;
      }

      // Always patch forceDisabled to ignore autosave lock IDs (all editors).
      const originalForceDisabled = editSourceCommand.forceDisabled?.bind(editSourceCommand);
      if (originalForceDisabled) {
        editSourceCommand.forceDisabled = (lockId) => {
          // Ignore locks from SourceEditingEnhanced autosave cycle.
          if (lockId === 'sourceEditingEnhanced' || lockId === 'pendingActions') {
            return;
          }
          return originalForceDisabled(lockId);
        };
      }

      // For mini-editor (RHP): also intercept isEnabled property.
      if (isMiniEditor) {
        let internalEnabled = editSourceCommand.isEnabled ?? true;

        Object.defineProperty(editSourceCommand, 'isEnabled', {
          get() {
            return internalEnabled;
          },
          set(value) {
            // Always keep enabled in non-read-only mini-editors.
            if (!value && !editor.isReadOnly) {
              internalEnabled = true;
            } else {
              internalEnabled = value;
            }
          },
          configurable: true
        });

        if (!editor.isReadOnly) {
          internalEnabled = true;
        }
      }

      // For main editor: replace _disableStack with a Set subclass that ignores
      // autosave lock entries, so autosave never locks the editSource command.
      if (!isMiniEditor) {
        const originalStack = editSourceCommand._disableStack;

        // A real Set subclass (not a Proxy) so all native Set methods/getters work.
        class FilteredDisableStack extends Set {
          add(id) {
            if (id === 'sourceEditingEnhanced' || id === 'pendingActions') {
              return this;
            }
            return super.add(id);
          }
        }

        const newStack = new FilteredDisableStack(
          [...originalStack].filter((id) => id !== 'sourceEditingEnhanced' && id !== 'pendingActions')
        );
        editSourceCommand._disableStack = newStack;

        // Clear any existing autosave locks and refresh.
        editSourceCommand.clearForceDisabled?.('sourceEditingEnhanced');
        editSourceCommand.clearForceDisabled?.('pendingActions');
        if (!editor.isReadOnly) {
          editSourceCommand.refresh?.();
        }
      }

      editSourceCommand.rhpPatched = true;
    };

    // Apply patch on ready.
    editor.on('ready', () => {
      setTimeout(() => patchEditSourceCommand(), 0);
    });

    // Re-enable on pending-action and data changes.
    if (isMiniEditor) {
      // RHP: direct re-enable (Object.defineProperty intercepts false -> true).
      pendingActions.on('change:hasAny', () => {
        const editSourceCommand = editor.commands.get('editSource');
        if (editSourceCommand && !editor.isReadOnly) {
          editSourceCommand.isEnabled = true;
        }
      });

      editor.model.document.on('change:data', () => {
        const editSourceCommand = editor.commands.get('editSource');
        if (editSourceCommand && !editor.isReadOnly) {
          editSourceCommand.isEnabled = true;
        }
      });
    } else {
      // Main editor: clear any autosave lock and re-enable on next tick.
      // This catches locks applied before our patch or via mechanisms we didn't intercept.
      pendingActions.on('change:hasAny', () => {
        setTimeout(() => {
          const cmd = editor.commands.get('editSource');
          if (cmd && !editor.isReadOnly) {
            cmd.clearForceDisabled('sourceEditingEnhanced');
            cmd.clearForceDisabled('pendingActions');
          }
        }, 0);
      });
    }
  }
}
